import React from 'react'
import i18next from 'i18next'
import get from 'lodash/get'
import { GridColDef } from '@microservices/wiskey-react-table-component'
import { ColumnDef } from '@tanstack/react-table'

import { JsxRegistry } from '@components/ScriptValueDialog'
import { VectorIconRenderer } from '@components/VectorIconRenderer'

import { getParamsString } from '@helpers'
import { FIELD_VALUE_TYPE } from '@constants'

import {
  ConfigField,
  GANTT_BIND_TYPE_LIST,
  GanttActionType,
  GanttColumnForm,
  GanttFormCommands,
  SectionType,
} from '@gantt/components/GanttCreateOrEdit/types'

import { FieldInfo } from '../../../../../wiskey-react-components/src'
import { ResourceColumnCell } from '../components/GanttCreateOrEdit/components/ResourceColumnCell'

const accessorFnActionValue = (originalRow: GanttActionType) => {
  const bindType = originalRow.actionField?.bindType
  const value = getParamsString(originalRow.actionField)

  switch (bindType) {
    case GANTT_BIND_TYPE_LIST.JSON:
      return JSON.stringify(value)
    case GANTT_BIND_TYPE_LIST.JS:
      return value
    case GANTT_BIND_TYPE_LIST.COMMANDS:
      return (value as GanttFormCommands).map(item => item.name)
    case GANTT_BIND_TYPE_LIST.FORM:
    case GANTT_BIND_TYPE_LIST.TOOLTIP:
    case GANTT_BIND_TYPE_LIST.CONTEXT_MENU:
      return value?.label

    default:
      return value
  }
}

const accessorFnSegmentValue = (originalRow: SectionType) => {
  const bindType = originalRow?.title?.bindType
  const value = get(originalRow, ['title', bindType || GANTT_BIND_TYPE_LIST.STATIC])

  switch (bindType) {
    case GANTT_BIND_TYPE_LIST.FIELD_ARRAY:
      return (value as ConfigField[]).map(item => item.pathStr).join(', ')
    default:
      return value?.pathStr ?? value
  }
}

const accessorFnFieldValue = (originalRow: GanttColumnForm) => {
  const fields = originalRow.fields

  return fields.map(item => {
    switch (item.bindType) {
      case GANTT_BIND_TYPE_LIST.STATIC:
        return item.static
      case GANTT_BIND_TYPE_LIST.FIELD:
        return item.field?.pathStr
      case GANTT_BIND_TYPE_LIST.JS:
        return item.js
    }
  })
}

const accessorFnBindType = (originalRow: GanttColumnForm) => {
  const fields = originalRow.fields

  return fields.map(item => item.bindType)
}

const accessorFnFieldValueType = (originalRow: GanttColumnForm) => {
  const fields = originalRow.fields

  return fields.map(item => {
    if (item.bindType === GANTT_BIND_TYPE_LIST.FIELD) {
      return item.field?.pathArray[item.field?.pathArray.length - 1].valueType
    }

    return FIELD_VALUE_TYPE.STRING
  })
}

const accessorFnTargetValue = (originalRow: GanttActionType) => {
  const target = originalRow.target

  if (!target) return ''

  if (Array.isArray(target)) {
    return target
      .map(item => {
        if (typeof item === 'object' && item !== null && 'label' in item) {
          return item.label
        }

        return item
      })
      .join(', ')
  }

  if (typeof target === 'object' && target !== null && 'label' in target) {
    return target.label
  }

  return target
}

export const GANTT_RESOURCE_COLUMN_ROW_HEIGHT = 60

export const GANTT_CONFIG_COLUMNS: ColumnDef<GanttColumnForm>[] = [
  {
    accessorKey: 'code',
    header: i18next.t('ganttCreate.columnForm.code'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('ganttCreate.columnForm.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'bindType',
    header: i18next.t('ganttCreate.columnForm.bindType'),
    meta: { flex: 1 },
    accessorFn: accessorFnBindType,
    cell: ({ getValue }) => {
      const values = getValue() as string[]

      return <ResourceColumnCell values={values} />
    },
  },
  {
    accessorKey: 'value',
    header: i18next.t('ganttCreate.columnForm.value'),
    meta: { flex: 1 },
    accessorFn: accessorFnFieldValue,
    cell: ({ getValue }) => {
      const values = getValue() as string[]

      return <ResourceColumnCell values={values} />
    },
  },
  {
    accessorKey: 'valueType',
    header: i18next.t('ganttCreate.columnForm.valueType'),
    meta: { flex: 1 },
    accessorFn: accessorFnFieldValueType,
    cell: ({ getValue }) => {
      const values = getValue() as string[]

      return <ResourceColumnCell values={values} />
    },
  },
  {
    accessorKey: 'pinnedColumn',
    header: i18next.t('ganttCreate.columnForm.pinnedColumn'),
    meta: { flex: 1 },
  },
]

export const GANTT_CONFIG_ACTION_COLUMNS = [
  {
    accessorKey: 'code',
    header: i18next.t('ganttCreate.actionForm.code'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('ganttCreate.actionForm.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'eventCode',
    header: i18next.t('ganttCreate.actionForm.eventCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'actionCode',
    header: i18next.t('ganttCreate.actionForm.actionCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'target',
    header: i18next.t('ganttCreate.actionForm.columnTarget'),
    meta: { flex: 1 },
    accessorFn: accessorFnTargetValue,
  },
  {
    accessorKey: 'actionField.bindType',
    header: i18next.t('ganttCreate.actionForm.valueType'),
    meta: { flex: 1 },
  },
  {
    header: i18next.t('ganttCreate.actionForm.value'),
    meta: { flex: 1 },
    accessorFn: accessorFnActionValue,
  },
]

export const GANTT_CONFIG_ACTION_TIMELINE_COLUMNS = [
  {
    accessorKey: 'code',
    header: i18next.t('ganttCreate.actionForm.code'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title',
    header: i18next.t('ganttCreate.actionForm.title'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'eventCode',
    header: i18next.t('ganttCreate.actionForm.eventCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'actionCode',
    header: i18next.t('ganttCreate.actionForm.actionCode'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'target',
    header: i18next.t('ganttCreate.actionForm.segmentTarget'),
    meta: { flex: 1 },
    accessorFn: accessorFnTargetValue,
  },
  {
    accessorKey: 'actionField.bindType',
    header: i18next.t('ganttCreate.actionForm.valueType'),
    meta: { flex: 1 },
  },
  {
    header: i18next.t('ganttCreate.actionForm.value'),
    meta: { flex: 1 },
    accessorFn: accessorFnActionValue,
  },
]

export const GANTT_SEGMENT_COLUMNS = [
  {
    accessorKey: 'link.systemName',
    header: i18next.t('ganttCreate.timelineForm.systemName'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'link.axisX.field.pathStr',
    header: i18next.t('ganttCreate.objectLinks'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'datetimeStart.field.pathStr',
    header: i18next.t('ganttCreate.timelineForm.dateTimeStart'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'datetimeEnd.field.pathStr',
    header: i18next.t('ganttCreate.timelineForm.dateTimeEnd'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'duration.field.pathStr',
    header: i18next.t('ganttCreate.timelineForm.duration'),
    meta: { flex: 1 },
  },
  {
    accessorKey: 'title.static.pathStr',
    header: i18next.t('ganttCreate.timelineForm.title'),
    meta: { flex: 1 },
    accessorFn: accessorFnSegmentValue,
  },
]

export const GANTT_LIST_COLUMNS: GridColDef[] = [
  {
    field: 'id',
    headerName: i18next.t('configGantt.ganttList.id'),
  },
  {
    field: 'title',
    headerName: i18next.t('configGantt.ganttList.title'),
    flex: 1,
  },
  {
    field: 'resourceObjectTitle',
    headerName: i18next.t('configGantt.ganttList.resourceObjectCode'),
    flex: 1,
  },
  {
    field: 'timelineObjects',
    headerName: i18next.t('configGantt.ganttList.timelineObjectCode'),
    flex: 1,
    valueFormatter: originalValue => {
      const value: Record<string, string> = originalValue

      return Object.values(value).join(', ')
    },
  },
]

export const JSX_REGISTRY: JsxRegistry = {
  [GANTT_BIND_TYPE_LIST.ICON]: {
    name: 'Icon',
    description: 'Компонент для отрисовки векторных иконок системы',
    render: (props: any) => React.createElement(VectorIconRenderer, { icon: props }),
    schema: [
      { name: 'type', type: 'string' },
      { name: 'value', type: 'string' },
      { name: 'size', type: 'number' },
      { name: 'color', type: 'string', optional: true },
    ],
  },
}

export const JSX_ICON_FIELDS_INFO: FieldInfo[] = [
  {
    key: 'srcObj',
    name: 'Current element data',
    children: [
      {
        key: '{\n  srcObj.name\n}',
        name: 'Show object name',
      },
      {
        key: '{\n  srcObj.value > 0 ?\n  "+" + srcObj.value :\n  srcObj.value\n}',
        name: 'Add plus sign to positive values',
      },
    ],
  },
  {
    key: 'moment',
    name: 'Date and time library',
    children: [
      {
        key: '{\n  moment().format("DD.MM.YY")\n}',
        name: 'Show current date (DD.MM.YY)',
      },
      {
        key: '{\n  moment(srcObj.startDate).format("MMMM Do")\n}',
        name: 'Show date as month and day',
      },
    ],
  },
  {
    key: '<div></div>',
    name: 'Layout and styling containers',
    children: [
      {
        key: '<div \n  style={{\n    width: 20, \n    height: 20, \n    borderRadius: "50%", \n    border: "1px solid black", \n    backgroundColor: "red" \n  }} \n/>',
        name: 'Round indicator with border',
      },
      {
        key: '<div \n  style={{\n    display: "flex", \n    flexDirection: "row", \n    gap: "8px", \n    alignItems: "center" \n  }}\n>\n  {1}\n  {2}\n</div>',
        name: 'Align elements in a row',
      },
    ],
  },
  {
    key: '{\n  (() => {\n    const value = 1;\n    return value;\n  })()\n}',
    name: 'Logic block for calculations',
    children: [
      {
        key: '{\n  (() => {\n    if (srcObj.name === "Card") return "Urgent";\n    if (srcObj.name === "Settings") return "System";\n    return "Normal";\n  })()\n}',
        name: 'Return text using "if" conditions',
      },
      {
        key: '{\n  (() => {\n    if (srcObj.priority > 5) {\n      return <Icon type="MaterialIcons" value="PriorityHigh" color="#FF0000" size={24} />;\n    }\n    return <Icon type="MaterialIcons" value="LowPriority" color="#008000" size={24} />;\n  })()\n}',
        name: 'Return different icons using "if" conditions',
      },
    ],
  },
  {
    key: '<Icon type="MaterialIcons" value="Abc" size={24} color="#000000" />',
    name: 'Icon with custom settings',
  },
]
